import YAML from 'yaml';

type ValidationResult = { valid: true } | { valid: false; errors: string[] };

const RESTRICTED_PROPERTIES = ['build', 'pull_policy', 'privileged', 'cap_add'] as const;
const RESERVED_PORTS = ['34730', '34731', '34732', '34733', '34734', '34735', '34736'];
const SECRET_NAME_PATTERN = /^EMMA_WSS_[a-zA-Z0-9_]*$/;
const NON_ASCII = /[^\0-\x7F]/;

function checkAscii(parsed: Record<string, unknown>, errors: string[]) {
  if (NON_ASCII.test(JSON.stringify(parsed))) {
    errors.push('File contains non-ASCII characters');
  }
}

function parseYaml(fileText: string): Record<string, unknown> | string {
  let parsed: unknown;
  try {
    parsed = YAML.parse(fileText);
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    return `Invalid YAML: ${message}`;
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return 'Invalid YAML: file must contain a YAML mapping';
  }

  return parsed as Record<string, unknown>;
}

function getHostPort(port: unknown): string | undefined {
  if (typeof port === 'string') return port.split(':')[0];
  if (typeof port === 'object' && port !== null && 'published' in port) {
    return String((port as { published: unknown }).published);
  }
  return undefined;
}

function validateService(
  name: string,
  service: Record<string, unknown>,
  registryUrl: string | undefined,
  errors: string[]
) {
  if (!service.image || typeof service.image !== 'string') {
    errors.push(`Service '${name}': missing a required 'image' field`);
  } else if (registryUrl && !service.image.startsWith(registryUrl)) {
    errors.push(`Service '${name}': image must start with '${registryUrl}'`);
  }

  for (const prop of RESTRICTED_PROPERTIES) {
    if (service[prop] !== undefined && service[prop] !== null && service[prop] !== false) {
      errors.push(
        `Service '${name}': '${prop}' is a restricted property. Remove it or contact the EMMA team`
      );
    }
  }

  if (!Array.isArray(service.ports)) return;
  for (const port of service.ports) {
    const hostPort = getHostPort(port);
    if (hostPort && RESERVED_PORTS.includes(hostPort)) {
      errors.push(
        `Service '${name}': port ${hostPort} is reserved. Ports 34730-34736 are used by EMMA`
      );
    }
  }
}

function validateSecrets(secrets: Record<string, unknown>, errors: string[]) {
  for (const [key, value] of Object.entries(secrets)) {
    if (typeof value !== 'object' || value === null) {
      errors.push(`Secret '${key}': must be a mapping with an 'environment' field`);
      continue;
    }
    const env = (value as Record<string, unknown>).environment;
    if (typeof env !== 'string') {
      errors.push(`Secret '${key}': missing required 'environment' field`);
      continue;
    }
    if (!SECRET_NAME_PATTERN.test(env)) {
      errors.push(
        `Secret '${key}' environment value '${env}' must match 'EMMA_WSS_' followed by only letters, digits, and underscores`
      );
    }
  }
}

export function validateDockerCompose(fileText: string): ValidationResult {
  const errors: string[] = [];

  const doc = parseYaml(fileText);
  if (typeof doc === 'string') {
    return { valid: false, errors: [doc] };
  }

  checkAscii(doc, errors);

  if (!doc.services || typeof doc.services !== 'object' || Array.isArray(doc.services)) {
    errors.push("Missing 'services' key. The file must define at least one service");
    return { valid: false, errors };
  }

  const services = doc.services as Record<string, Record<string, unknown>>;
  const registryUrl = import.meta.env.VITE_EMMA_DOCKER_REGISTRY_URL as string | undefined;

  for (const [name, service] of Object.entries(services)) {
    if (typeof service !== 'object' || service === null) {
      errors.push(`Service '${name}': must be a mapping, got ${typeof service}`);
      continue;
    }
    validateService(name, service, registryUrl, errors);
  }

  if (doc.secrets && typeof doc.secrets === 'object' && !Array.isArray(doc.secrets)) {
    validateSecrets(doc.secrets as Record<string, unknown>, errors);
  }

  return errors.length > 0 ? { valid: false, errors } : { valid: true };
}
